// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByNodeId, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Link, { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import { linkStyles } from "@store/Themes/common/mixins/link";
import { menuSecondaryNavigationItemStyles } from "@store/Themes/common/mixins/menu";
import React, { FC, useState } from "react";
import { css } from "styled-components";

export type WebsiteMenuLinkModel = LinkModel & {
    fields: { isCurrent?: boolean };
};

const enum fields {
    links = "links",
    showSwitchModal = "showSwitchModal",
    modalHeadline = "modalHeadline",
    modalBody = "modalBody",
    modalCtaLabel = "modalCtaLabel",
    modalCancelLabel = "modalCancelLabel",
}

const enum linksFields {
    destination = "destination",
    openInNewWindow = "openInNewWindow",
    overriddenTitle = "overriddenTitle",
    isCurrent = "isCurrent",
}

export interface WebsiteMenuProps extends WidgetProps {
    fields: {
        [fields.links]: WebsiteMenuLinkModel[];
        [fields.showSwitchModal]: boolean;
        [fields.modalHeadline]: string;
        [fields.modalBody]: string;
        [fields.modalCtaLabel]: string;
        [fields.modalCancelLabel]: string;
    };
}

export interface WebsiteMenuStyles {
    wrapper?: InjectableCss;
    link?: LinkPresentationProps;
    switchSiteModal?: TwoButtonModalStyles;
}

export const websiteMenuStyles: WebsiteMenuStyles = {
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    link: {
        css: css`
            ${menuSecondaryNavigationItemStyles({ separatorSize: "1px", color: "menu.secondaryNavigation.contrast" })}
            ${linkStyles("menu.secondaryNavigation.contrast", "menu.secondaryNavigation.hover", false, true)}
            font-size: ${x => x.theme.design.typography.size.smallest};
            letter-spacing: 0.2em;

            &.on {
                color: ${getColor("menu.secondaryNavigation.hover")};
                font-weight: ${x => x.theme.design.typography.weight.bold};
                text-decoration: none;
            }
        `,
    },
    switchSiteModal: {
        modal: { sizeVariant: "medium" },
        cancelButton: { variant: "secondary" },
    },
};

const baseStyles = websiteMenuStyles;

export const WebsiteMenu: FC<WebsiteMenuProps> = ({ fields }) => {
    const { links, hasMissingCategories } = useGetLinks(fields.links, x => x.fields.destination);
    const [styles] = React.useState(() => mergeToNew(baseStyles, {}));
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pendingHref, setPendingHref] = useState<string | null>(null);
    const [pendingTitle, setPendingTitle] = useState<string>("");
    const [pendingTarget, setPendingTarget] = useState<"_self" | "_blank">("_self");

    if (hasMissingCategories) return null;
    if (!links.length) return null;

    const openUrl = (href: string, target: "_self" | "_blank") => {
        if (target === "_blank") {
            window.open(href, "_blank", "noopener,noreferrer");
        } else {
            window.location.assign(href);
        }
    };

    const onConfirm = () => {
        if (pendingHref) {
            openUrl(pendingHref, pendingTarget);
        }
        setIsModalOpen(false);
    };

    const onClose = () => {
        setIsModalOpen(false);
        setPendingHref(null);
        setPendingTitle("");
    };

    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                {links.map(({ title, url }, index) => {
                    if (!url) return null;

                    const { openInNewWindow, overriddenTitle, isCurrent } = fields.links[index].fields;
                    const _title: string = overriddenTitle || title || "";
                    const target: "_self" | "_blank" = openInNewWindow ? "_blank" : "_self";
                    const shouldIntercept = !isCurrent && fields.showSwitchModal;

                    return (
                        <Link
                            {...styles.link}
                            className={isCurrent ? "on" : ""}
                            href={shouldIntercept ? undefined : url}
                            key={`${url}.${_title}`}
                            target={shouldIntercept ? undefined : target}
                            onClick={e => {
                                if (!shouldIntercept) return;
                                e.preventDefault();
                                setPendingHref(url);
                                setPendingTitle(_title);
                                setPendingTarget(target);
                                setIsModalOpen(true);
                            }}
                            onKeyDown={e => {
                                if (!shouldIntercept) return;
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    setPendingHref(url);
                                    setPendingTitle(_title);
                                    setPendingTarget(target);
                                    setIsModalOpen(true);
                                }
                            }}
                        >
                            {_title}
                        </Link>
                    );
                })}
            </StyledWrapper>
            <TwoButtonModal
                {...styles.switchSiteModal}
                modalIsOpen={isModalOpen}
                headlineText={fields.modalHeadline.replace("{0}", pendingTitle)}
                messageText={fields.modalBody.replace("{0}", pendingTitle)}
                cancelButtonText={fields.modalCancelLabel}
                submitButtonText={fields.modalCtaLabel}
                onCancel={onClose}
                onSubmit={onConfirm}
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: WebsiteMenu,
    definition: {
        displayName: "Website Menu",
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: linksFields.destination,
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: linksFields.overriddenTitle,
                        displayName: "Title",
                        editorTemplate: "TextField",
                        isRequired: true,
                        defaultValue: "",
                    },
                    {
                        name: linksFields.isCurrent,
                        displayName: "Is Current Website",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: linksFields.openInNewWindow,
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                name: fields.showSwitchModal,
                displayName: "Show Switch Modal",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                tooltip:
                    "Enable/disable the confirmation Modal when the user clicks a link that navigates to another site.",
            },
            {
                name: fields.modalHeadline,
                displayName: "Headline",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Proceed to {0}",
                isRequired: true,
                isVisible: x => x.fields.showSwitchModal,
                tooltip:
                    "Use {0} to automatically insert the clicked link's title. If you remove {0}, the text will be static.",
            },
            {
                name: fields.modalBody,
                displayName: "Message",
                editorTemplate: "MultilineTextField",
                fieldType: "Contextual",
                defaultValue: "You'll be redirected to {0}. Each site has its own cart and session.",
                isRequired: true,
                isVisible: x => x.fields.showSwitchModal,
                tooltip:
                    "{0} is automatically populated with the selected link title; delete {0} for a non-variable message.",
            },
            {
                name: fields.modalCtaLabel,
                displayName: "CTA Label",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "Continue",
                isRequired: true,
                isVisible: x => x.fields.showSwitchModal,
                tooltip: "Primary action button label.",
            },
            {
                name: fields.modalCancelLabel,
                displayName: "Cancel Label",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "Stay here",
                isRequired: true,
                isVisible: x => x.fields.showSwitchModal,
                tooltip: "Cancel button label.",
            },
        ],
    },
};

export default widgetModule;
